import { useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';

import type { IntakeItem } from '../types';

import { IntakeItemDetailsDialog, type IntakeDocumentAccess } from './IntakeItemDetailsDialog';
import { IntakeStatusChip } from './IntakeStatusChip';

interface IntakeItemRowProps {
  item: IntakeItem;
  compact?: boolean;
  documentAccess?: IntakeDocumentAccess;
  onUploadRequest?: (item: IntakeItem) => void;
}

const PROVENANCE_LABELS = {
  ai: 'AI',
  manual: 'Manual',
  source_system: 'On file',
} as const;

const ACTION_BUTTON_SX = {
  appearance: 'none',
  border: `1px solid ${ELIG_TOKENS.lineStrong}`,
  borderRadius: 999,
  bgcolor: ELIG_TOKENS.card,
  color: ELIG_TOKENS.midnight,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 12,
  fontWeight: 850,
  lineHeight: 1,
  px: 1,
  py: 0.55,
  whiteSpace: 'nowrap',
  '&:hover': {
    bgcolor: 'rgba(28,30,133,0.075)',
  },
} as const;

function getStatusIcon(item: IntakeItem) {
  if (item.status === 'accepted' || item.status === 'staff_complete') {
    return CheckCircleOutlineOutlinedIcon;
  }
  if (
    item.status === 'ai_flagged' ||
    item.status === 'needs_attention' ||
    item.status === 'expiring'
  ) {
    return ErrorOutlineOutlinedIcon;
  }
  if (
    item.status === 'in_review' ||
    item.status === 'submitted' ||
    item.status === 'staff_in_progress'
  ) {
    return AccessTimeOutlinedIcon;
  }
  if (item.type === 'document') {
    return UploadFileOutlinedIcon;
  }
  if (item.type === 'information') {
    return EditNoteOutlinedIcon;
  }
  return RadioButtonUncheckedOutlinedIcon;
}

export function IntakeItemRow({
  item,
  compact = false,
  documentAccess,
  onUploadRequest,
}: IntakeItemRowProps) {
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const Icon = getStatusIcon(item);
  const isIssue =
    item.status === 'ai_flagged' || item.status === 'needs_attention' || item.status === 'expiring';
  const isMissing = item.status === 'missing';
  const isComplete =
    item.status === 'accepted' ||
    item.status === 'staff_complete' ||
    item.status === 'not_applicable';
  const isPending =
    item.status === 'submitted' ||
    item.status === 'in_review' ||
    item.status === 'staff_in_progress';
  const isJotPsychOwned = item.owner === 'jotpsych' || item.type === 'staff_task';
  const canUpload = !isJotPsychOwned && item.type === 'document' && Boolean(onUploadRequest);
  const canEnterInformation = !isJotPsychOwned && item.type === 'information';
  const issueColor = item.status === 'needs_attention' ? ELIG_TOKENS.red : ELIG_TOKENS.amber;
  const accentColor = isIssue
    ? issueColor
    : isMissing
      ? ELIG_TOKENS.midnight
      : isComplete
        ? ELIG_TOKENS.green
        : ELIG_TOKENS.gray;
  const stripeWidth = isIssue ? 8 : isMissing ? 3 : 0;
  const extractedFields =
    item.evidenceSummary.extracted_fields &&
    typeof item.evidenceSummary.extracted_fields === 'object'
      ? (item.evidenceSummary.extracted_fields as Record<string, unknown>)
      : {};
  const hasEvidence =
    item.sourceRefs.length > 0 ||
    Object.keys(extractedFields).length > 0 ||
    Boolean(item.evidenceSummary.value_preview) ||
    Boolean(item.evidenceSummary.label);

  return (
    <Box
      sx={{
        position: 'relative',
        border: isIssue
          ? `1px solid ${issueColor}`
          : isMissing
            ? `1px solid ${ELIG_TOKENS.lineStrong}`
            : isComplete
              ? `1px solid ${ELIG_TOKENS.line}`
              : `1px solid ${ELIG_TOKENS.line}`,
        borderRadius: '6px',
        bgcolor: isIssue
          ? item.status === 'needs_attention'
            ? ELIG_TOKENS.redTint
            : ELIG_TOKENS.amberTint
          : isMissing
            ? 'rgba(28,30,133,0.035)'
            : isComplete
              ? ELIG_TOKENS.grayTint
              : ELIG_TOKENS.card,
        opacity: isComplete ? 0.72 : 1,
        overflow: 'hidden',
        boxShadow: isIssue ? '0 4px 14px rgba(15, 23, 42, 0.08)' : 'none',
        transition: 'background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease',
        '&:hover': {
          bgcolor: isIssue
            ? item.status === 'needs_attention'
              ? ELIG_TOKENS.redSoft
              : ELIG_TOKENS.amberSoft
            : isComplete
              ? ELIG_TOKENS.graySoft
              : 'rgba(28,30,133,0.045)',
          boxShadow: isComplete ? 'none' : '0 6px 16px rgba(15, 23, 42, 0.10)',
          opacity: 1,
        },
      }}
    >
      {stripeWidth > 0 && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            insetBlock: 0,
            left: 0,
            width: stripeWidth,
            bgcolor: accentColor,
            pointerEvents: 'none',
          }}
        />
      )}
      <Stack
        direction="row"
        spacing={1.25}
        sx={{
          alignItems: 'flex-start',
          pl: compact ? 1.5 : 2,
          pr: compact ? 1.5 : 2,
          py: compact ? 1.25 : 1.75,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 999,
            display: 'grid',
            placeItems: 'center',
            bgcolor: isIssue
              ? item.status === 'needs_attention'
                ? ELIG_TOKENS.redSoft
                : ELIG_TOKENS.amberSoft
              : isComplete
                ? ELIG_TOKENS.greenSoft
                : 'rgba(28,30,133,0.075)',
            color: accentColor,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography
              variant="body2"
              sx={{ color: ELIG_TOKENS.ink, fontWeight: 750, lineHeight: 1.25 }}
            >
              {item.label}
            </Typography>
            {item.provenance && (
              <Typography
                variant="caption"
                sx={{ color: ELIG_TOKENS.inkFaint, fontWeight: 700, lineHeight: 1 }}
              >
                {PROVENANCE_LABELS[item.provenance]}
              </Typography>
            )}
          </Stack>

          {item.description && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.25, color: ELIG_TOKENS.inkSoft }}
            >
              {item.description}
            </Typography>
          )}

          {item.issueNote && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, color: issueColor, fontWeight: 650 }}
            >
              {item.issueNote}
            </Typography>
          )}

          {Boolean(item.evidenceSummary.value_preview) && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, color: ELIG_TOKENS.inkSoft, fontWeight: 650 }}
            >
              {String(item.evidenceSummary.value_preview)}
            </Typography>
          )}
        </Box>

        <Stack spacing={0.65} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
          <IntakeStatusChip status={item.status} />
          {(hasEvidence || canEnterInformation) && (
            <Box component="button" onClick={() => setDetailsOpen(true)} sx={ACTION_BUTTON_SX}>
              {hasEvidence ? 'View' : 'Add info'}
            </Box>
          )}
          {isJotPsychOwned ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              no action
            </Typography>
          ) : canUpload ? (
            <Box component="button" onClick={() => onUploadRequest?.(item)} sx={ACTION_BUTTON_SX}>
              {isPending || isComplete ? 'Replace' : 'Upload'}
            </Box>
          ) : item.instruction ? (
            <Box component="button" onClick={() => setInstructionOpen(true)} sx={ACTION_BUTTON_SX}>
              how to find
            </Box>
          ) : item.actionLabel ? (
            <Typography
              variant="caption"
              sx={{
                border: `1px solid ${ELIG_TOKENS.lineStrong}`,
                borderRadius: 999,
                bgcolor: ELIG_TOKENS.card,
                color: ELIG_TOKENS.midnight,
                fontWeight: 850,
                lineHeight: 1,
                px: 1,
                py: 0.55,
                whiteSpace: 'nowrap',
              }}
            >
              {item.actionLabel}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      {item.instruction && (
        <Dialog
          open={instructionOpen}
          onClose={() => setInstructionOpen(false)}
          fullWidth
          maxWidth="xs"
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 2.5,
              border: `1px solid ${ELIG_TOKENS.lineStrong}`,
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                px: 2.25,
                py: 2,
                borderBottom: `1px solid ${ELIG_TOKENS.line}`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                  How to find
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.25, fontSize: 18 }}>
                  {item.label}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="Close instructions"
                onClick={() => setInstructionOpen(false)}
              >
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Box sx={{ px: 2.25, py: 2.25 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                {item.instruction}
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      )}
      <IntakeItemDetailsDialog
        open={detailsOpen}
        item={item}
        documentAccess={documentAccess}
        onClose={() => setDetailsOpen(false)}
      />
    </Box>
  );
}
